'use client'

import { useEffect } from 'react'

import { getClientAnalytics } from '../client-instance'

export interface AnalyticsSessionContext {
  workspaceId?: string
  memberId?: string
  appVersion?: string
  userRole?: string | null
  isImpersonated?: boolean
  disableLegacyAccess?: boolean
  planViewMvp?: boolean
}

/**
 * Sets session-scoped context on every analytics event.
 *
 * This is independent of user identity — the same user may switch app versions
 * or workspaces, so this reflects the current session rather than the person.
 *
 * Absent values are forwarded as `undefined`, which adapters treat as "remove
 * this property" — so context doesn't leak across workspace or session changes.
 */
export function useAnalyticsSessionContext(
  context: AnalyticsSessionContext | undefined,
): void {
  const {
    workspaceId,
    memberId,
    appVersion,
    userRole,
    isImpersonated,
    disableLegacyAccess,
    planViewMvp,
  } = context ?? {}

  useEffect(() => {
    getClientAnalytics().setDefaultProperties({
      workspace_id: workspaceId,
      member_id: memberId,
      app_version: appVersion,
      user_role: userRole ?? undefined,
      is_impersonated: isImpersonated,
      disable_legacy_access: disableLegacyAccess,
      plan_view_mvp: planViewMvp,
    })
  }, [
    workspaceId,
    memberId,
    appVersion,
    userRole,
    isImpersonated,
    disableLegacyAccess,
    planViewMvp,
  ])
}
